import { useContext } from 'react';
import { useAuthenticated } from 'react-admin';

import { AppContext } from '@app/contexts';
import { useGenericList } from '@app/hooks/useGenericList';
import { CASLAction, useAbility } from '@app/utils/authUtils';
import { getFieldOnMainLanguage } from '@app/utils/languages';
import { getTypeLabel } from '@app/utils/questionUtils';
import { makeCommaSeparatedString } from '@app/utils/stringUtils';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';

import { GenericList } from '@app/components/common/GenericList';
import { ColumnTypes, GenericObject } from '@app/components/common/Table/DefaultTable/types';

export const ListQuestions = () => {
  const { handleChangeLoading } = useContext(AppContext);
  const ability = useAbility();

  const { isLoading, data, total, handleChangeFilters, handleChangeSort, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    useGenericList('questions', { sort: { field: 'date_created', order: 'DESC' } });

  /**
   * List[X] pages require manual auth check for some reason.
   * This involves the `checkAuth` method in auth.ts.
   */
  useAuthenticated();

  const columns = [
    {
      label: 'ID',
      key: 'question',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => `${getFieldOnMainLanguage(row?.question)}`,
      sort: (order: string) => {
        handleChangeSort({ field: 'question', order });
      },
    },
    {
      label: 'NAME',
      key: 'type',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => getTypeLabel(row),
      sort: (order: string) => {
        handleChangeSort({ field: 'type', order });
      },
    },
    {
      label: 'VALUES',
      key: 'responses',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => makeCommaSeparatedString(getFieldOnMainLanguage(row?.responses).split(',')),
    },
    {
      label: 'USER PROFILE',
      key: 'is_user_profile',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) =>
        row.is_user_profile ? <CheckIcon color="success" /> : <CloseIcon color="error" />,
      sort: (order: string) => {
        handleChangeSort({ field: 'is_user_profile', order });
      },
    },
  ];

  if (ability.can(CASLAction.WRITE, 'questions')) {
    columns.push({
      label: 'PUBLISHED',
      key: 'status',
      type: ColumnTypes.STATUS,
      // @ts-ignore definitely valid
      publishedKey: 'status',
      isPublished: (row: GenericObject) => row.status === 'ACTIVE',
      getNewPublishedStatus: (row: GenericObject) => (row.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE'),
      sort: (order: string) => {
        handleChangeSort({ field: 'status', order });
      },
    });
  }

  return (
    <GenericList
      isListLoading={isLoading}
      rows={data}
      hideSearch={true}
      generalFilterKey={'name'}
      resourceSingular="question"
      handleChangeLoading={handleChangeLoading}
      columns={columns}
      resource="questions"
      title="Questions"
      page={page}
      rowsPerPage={rowsPerPage}
      handleChangePage={handleChangePage}
      total={total}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      handleChangeFilters={handleChangeFilters}
    />
  );
};
